import { Card, Button, Form, Row, Modal } from "react-bootstrap";
import MaterialTable, {
    Column,
    EditComponentProps,
} from "@material-table/core";
import React, { useState, useEffect, useCallback } from "react";
import * as APIModels from "../../../../models";
import { FileService } from "../../../../services/SubmissionService";
import { useDropzone } from "react-dropzone";
import Select, { ActionMeta } from "react-select";
import { TylerDataService } from "../../../../services/DataService";
import { responsiveProperty } from "@mui/material/styles/cssUtils";
import { doc } from "prettier";
import { Endpoints } from "../../../../helpers/Endpoints";

type MyOptionType = { label: string; value: string };

export default function DocumentModal({
    show,
    document,
    countyId,
    caseTypeId,
    updateDocument,
    newCase,
    submission,
    close,
}: {
    show: boolean;
    document?: APIModels.Document | undefined;
    countyId: string;
    caseTypeId: string;
    updateDocument: (document: APIModels.Document) => void;
    newCase: boolean;
    submission: APIModels.Submission;
    close: () => void;
}): React.ReactElement {
    const placeholderOption = [
        {
            label: "Please Choose a Filing Code",
            value: APIModels.EmptyGuid,
        } as MyOptionType,
    ] as MyOptionType[];
    const [updatedDocument, setUpdatedDocument] = useState(
        document ?? new APIModels.Document()
    );

    const [file, setFile] = useState<APIModels.FileInfo>({
        filename: "",
        size: 0,
    });
    const filenameRegex = /[^a-zA-Z0-9_-]+/g;

    const [errorMessage, setErrorMessage] = useState("");
    const [warning, setWarning] = useState("");
    // Option States
    const [updatedFilingCodeOptions, setUpdatedFilingCodeOptions] = useState(
        [] as MyOptionType[]
    );
    const [updatedFilingCompOptions, setUpdatedFilingCompOptions] = useState(
        [] as MyOptionType[]
    );
    const [updatedSecOptions, setUpdatedSecOptions] = useState(
        [] as MyOptionType[]
    );
    const [securityRefrence, setSecurityRefrence] = useState(
        [] as APIModels.DocumentType[]
    );
    const [updatedOptServOpt, setUpdatedOptServOpt] = useState(
        [] as MyOptionType[]
    );
    // Form Choices
    const [feeTemp, setFeeTemp] = useState(new APIModels.Fee());

    const [optionalServiceTemp, setOptionalServiceTemp] = useState(
        new APIModels.OptionalServicesCode()
    );

    let options = [] as MyOptionType[];
    let options1 = [] as MyOptionType[];

    useEffect(() => {
        if (!document) setUpdatedDocument({ submissionID: submission.id! });
        else setUpdatedDocument(document);
        console.log("Document Modal: ", updatedDocument);
    }, [document]);

    // Update Filing Codes
    useEffect(() => {
        if (caseTypeId != APIModels.EmptyGuid) {
            TylerDataService.getFilingCodes({
                caseTypeId: caseTypeId,
                newCase: newCase,
            }).then(
                (resp) => {
                    if (resp.length > 0) {
                        options = resp.map((codes) => {
                            return { label: codes.name!, value: codes.id! };
                        });
                        setUpdatedFilingCodeOptions(options);
                        options = [] as MyOptionType[];
                    } else {
                        setErrorMessage(
                            "An error occured in accessing the api for the Filing Codes."
                        );
                    }
                },
                (error) => {
                    setErrorMessage(error);
                }
            );
            if (errorMessage !== "") {
                console.log(errorMessage);
                setErrorMessage("");
            }
        }
    }, [caseTypeId]);

    // Update Security
    useEffect(() => {
        if (
            updatedDocument.filingCodeID &&
            updatedDocument.filingCodeID != APIModels.EmptyGuid
        ) {
            setUpdatedSecOptions([]);

            TylerDataService.getDocumentTypesByFilingCode({
                countyId: countyId,
                filingCode: updatedDocument.filingCodeID ?? "",
            }).then(
                (resp) => {
                    if (resp.length > 0) {
                        setSecurityRefrence(resp);
                        options1 = resp.map((sec) => {
                            return { label: sec.name!, value: sec.id! };
                        });
                        setUpdatedSecOptions(options1);
                        options1 = [] as MyOptionType[];
                    } else {
                        setErrorMessage(
                            "An error occured in accessing the api when accessing the Document Types."
                        );
                    }
                },
                (error) => {
                    setErrorMessage(error);
                }
            );

            if (errorMessage !== "") {
                console.log(errorMessage);
                setErrorMessage("");
            }
        }
    }, [updatedDocument.filingCodeID]);

    // Update Optional Services on Filing Code Change
    useEffect(() => {
        if (
            updatedDocument.filingCodeID &&
            updatedDocument.filingCodeID != APIModels.EmptyGuid
        ) {
            TylerDataService.getOptServByFilingCode({
                countyId: countyId,
                filingCodeId: updatedDocument.filingCodeID ?? "",
            })
                .then(
                    (resp) => {
                        if (resp.length > 0) {
                            options = resp.map((opts) => {
                                return { label: opts.name!, value: opts.id! };
                            });
                            setUpdatedOptServOpt(options);
                            options = [] as MyOptionType[];
                        } else {
                            setErrorMessage(
                                "An error occured in accessing the api when accessing the Optional Services."
                            );
                        }
                    },
                    (error) => {
                        setErrorMessage(error);
                    }
                )
                .then(() => {
                    TylerDataService.getFilingComponentsByFilingCode({
                        filingCodeId: updatedDocument.filingCodeID ?? "",
                    }).then((resp) => {
                        if (resp.length > 0) {
                            options = resp.map((opts) => {
                                return { label: opts.name!, value: opts.id! };
                            });
                            setUpdatedFilingCompOptions(options);
                            options = [] as MyOptionType[];
                        } else {
                            setErrorMessage(
                                "An error occured in accessing the api when accessing the Filing Components."
                            );
                        }
                    });
                });
            if (errorMessage !== "") {
                console.log(errorMessage);
                setErrorMessage("");
            }
        }
    }, [updatedDocument.filingCodeID]);

    // On Changes
    const onCodeChange = (value: MyOptionType | null) => {
        setUpdatedDocument({
            ...updatedDocument,
            filingCode: value?.label,
            filingCodeID: value?.value,
        });
    };

    const onCompChange = (value: MyOptionType | null) => {
        setUpdatedDocument({
            ...updatedDocument,
            filingComponentID: value?.value,
        });
    };

    const onSecChange = (value: MyOptionType | null) => {
        // get the security associated with the id
        setUpdatedDocument({ ...updatedDocument, securityID: value?.value });
    };

    const onRefChange = (value: string) => {
        setUpdatedDocument({ ...updatedDocument, referenceNumber: value });
    };

    const onOptServChange = (value: MyOptionType | null) => {
        const tempFee = new APIModels.Fee();
        optionalServiceTemp.id = value?.value;
        optionalServiceTemp.name = value?.label;
        tempFee.id = value?.value;
        tempFee.description = value?.label;
        setFeeTemp(tempFee);
    };

    const onQuantityChange = (value: number) => {
        optionalServiceTemp.quantity = value;
        feeTemp.quantity = value;
    };

    const onDescChange = (value: string) => {
        setUpdatedDocument({ ...updatedDocument, description: value });
    };

    const onAddButton = () => {
        if (!updatedDocument.fees) {
            updatedDocument.fees = [];
            updatedDocument.optionalServices = [];
        }
        if (feeTemp.description && feeTemp.quantity) {
            setWarning("");
            const documentFees = updatedDocument.fees;
            const documentOptions = updatedDocument.optionalServices;
            documentOptions?.push(optionalServiceTemp);
            documentFees.push(feeTemp);
            setUpdatedDocument({
                ...updatedDocument,
                fees: documentFees,
                optionalServices: documentOptions,
            });
            setFeeTemp(new APIModels.Fee());
        } else if (!feeTemp.description) {
            setWarning("Please Select a Service.");
        } else if (!feeTemp.amount) {
            setWarning("Please Select a Quantity.");
        }
    };

    const onFileUpload = (event: React.FormEvent) => {
        event.preventDefault();
        setErrorMessage("");

        const input = event.target as HTMLInputElement;

        if (!input.files?.length || input.files.length == 0) {
            return;
        }
        const f = input.files[0];
        if (f.name.includes(".zip")) {
            setErrorMessage("Batch File Not Enabled For Account");
        } else {
            setFile({ filename: f.name, size: f.size });

            if (
                !f.name.toLowerCase().includes(".zip") &&
                !f.name.toLowerCase().includes(".pdf") &&
                !f.name.toLowerCase().endsWith(".docx")
            ) {
                setErrorMessage(
                    "Invalid File Type. Please Upload a PDF or ZIP File or Docx"
                );
            } else {
                let sanatizedName = "";

                if (f.name.toLowerCase().includes(".zip")) {
                    sanatizedName =
                        f.name
                            .toLowerCase()
                            .split(".zip")[0]
                            .replace(filenameRegex, "") + ".zip";
                } else if (f.name.toLowerCase().includes(".docx")) {
                    sanatizedName =
                        f.name
                            .toLowerCase()
                            .split(".docx")[0]
                            .replace(filenameRegex, "") + ".docx";
                } else {
                    sanatizedName =
                        f.name
                            .toLowerCase()
                            .split(".pdf")[0]
                            .replace(filenameRegex, "") + ".pdf";
                }

                setUpdatedDocument({
                    ...updatedDocument,
                    filename: sanatizedName,
                    size: file.size,
                });
                console.log("UUID", updatedDocument.submissionID);
                FileService.upload({
                    fileServiceUrl: Endpoints.TylerFileService,
                    file: f,
                    filename: sanatizedName,
                    uuid: updatedDocument.submissionID,
                }).then(
                    (_resp) => {
                        setUpdatedDocument({
                            ...updatedDocument,
                            filename: sanatizedName,
                            size: file.size,
                        });
                    },
                    (error) => {
                        setUpdatedDocument({
                            ...updatedDocument,
                            filename: "Error Uploading",
                            size: 0,
                        });
                    }
                );
                input.value = "";
            }
        }
    };

    const editSecuritySelect = useCallback(
        (props: EditComponentProps<APIModels.Document>) => {
            return (
                <Select
                    id="selSecurityEdit"
                    name="selSecurityEdit"
                    value={updatedSecOptions.filter(
                        (c) => c.value === props.rowData.documentTypeID
                    )}
                    options={updatedSecOptions}
                    onChange={(
                        value: MyOptionType | null,
                        actionMeta: ActionMeta<MyOptionType>
                    ) => {
                        console.log(value);
                        props.rowData.documentTypeID = value?.value;
                        props.rowData.documentType = value?.label;
                        props.onChange(value?.label);
                    }}
                />
            );
        },
        [updatedSecOptions]
    );

    const editAttachmentUploadSelect = useCallback(
        (props: EditComponentProps<APIModels.Document>) => {
            return (
                <>
                    <input
                        id={
                            "attach-upload-" + props.rowData.attachments?.length
                        }
                        type="file"
                        style={{ display: "none" }}
                    />
                    <label
                        htmlFor={
                            "attach-upload-" + props.rowData.attachments?.length
                        }
                    >
                        {props.rowData.filename ?? "Click to Upload File"}
                    </label>
                </>
            );
        },
        [updatedSecOptions]
    );

    return (
        <Modal show={show} size="lg" centered>
            <Modal.Header>Document Information</Modal.Header>
            <Modal.Body>
                <div className="row">
                    <div className="col-6">
                        <div>
                            <label
                                htmlFor={"lead-filename"}
                                className="form-label"
                            >
                                Selected File:
                            </label>
                        </div>
                        <div style={{ textAlign: "center" }}>
                            <input
                                id="lead-filename"
                                name="lead-filename"
                                type="file"
                                className="upload"
                                onChange={onFileUpload}
                                style={{
                                    display: "none",
                                }}
                            />
                            <label htmlFor={"lead-filename"}>
                                {updatedDocument.filename !== null &&
                                updatedDocument.filename !== undefined &&
                                updatedDocument.filename !== ""
                                    ? updatedDocument.filename
                                    : " Click to Upload File"}
                            </label>
                        </div>
                    </div>
                    <div className="col-6">
                        <div>
                            <label
                                htmlFor={"selSecurity"}
                                className="form-label"
                            >
                                Filing Component Type:
                            </label>
                        </div>
                        <Select
                            id="selSecurity"
                            name="selSecurity"
                            value={updatedFilingCompOptions.filter(
                                (i, _v) =>
                                    i.value ===
                                    updatedDocument.filingComponentID?.toLowerCase()
                            )}
                            options={updatedFilingCompOptions}
                            onChange={(o, _m) => onCompChange(o)}
                        />
                    </div>
                </div>
                <div className="row mb-3">
                    <div className="col-6">
                        <label htmlFor="selFilingCode" className="form-label">
                            Filing Code
                        </label>
                        <Select
                            id="selFilingCode"
                            name="selFilingCode"
                            value={updatedFilingCodeOptions.filter(
                                (i, v) =>
                                    i.value ===
                                    updatedDocument.filingCodeID?.toLowerCase()
                            )}
                            options={updatedFilingCodeOptions}
                            onChange={(o, m) => onCodeChange(o)}
                        />
                    </div>
                    <div className="col-6">
                        <label htmlFor="selSecurity" className="form-label">
                            Security
                        </label>
                        <Select
                            id="selSecurity"
                            name="selSecurity"
                            value={updatedSecOptions.filter(
                                (i, v) =>
                                    i.value ===
                                    updatedDocument.securityID?.toLowerCase()
                            )}
                            options={
                                updatedSecOptions.length > 0
                                    ? updatedSecOptions
                                    : placeholderOption
                            }
                            onChange={(o, m) => onSecChange(o)}
                        />
                    </div>
                </div>
                <div className="row mb-3">
                    <div className="col-6">
                        <label htmlFor="txtClientRef" className="form-label">
                            Client Reference
                        </label>
                        <input
                            id="txtClientRef"
                            name="txtClientRef"
                            type="text"
                            className="form-control"
                            placeholder="Enter Client Reference"
                            value={updatedDocument.downloadCopyUrl}
                            onChange={(event) => {
                                onRefChange(event.currentTarget.value ?? "");
                            }}
                        />
                    </div>
                    <div className="col-6">
                        <label htmlFor="txtDocDesc" className="form-label">
                            Description
                        </label>
                        <input
                            id="txtDocDesc"
                            name="txtDocDesc"
                            type="text"
                            className="form-control"
                            placeholder="Enter Description"
                            value={updatedDocument.description}
                            onChange={(event) => {
                                onDescChange(event.currentTarget.value);
                            }}
                        />
                    </div>
                </div>
                <div className="row mb-3">
                    <div className="col-12">
                        <label htmlFor="txtDocDesc" className="form-label">
                            Selected Services
                        </label>
                        <table style={{ width: "100%" }}>
                            <thead>
                                <tr>
                                    <td style={{ width: "69%" }}>Service</td>
                                    <td>Quantity</td>
                                </tr>
                                {updatedDocument.fees?.length ? (
                                    updatedDocument.fees?.map((fee) => {
                                        return (
                                            <tr>
                                                <td>{fee?.description}</td>
                                                <td>{fee?.quantity}</td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td>
                                            Select an Optional Service{" "}
                                            <em>if needed.</em>
                                        </td>
                                        <td></td>
                                    </tr>
                                )}
                                <tr>
                                    <td></td>
                                    <td></td>
                                </tr>
                            </thead>
                        </table>
                    </div>
                </div>
                <div className="row mb-3">
                    <div className="col-8">
                        <label htmlFor="selServices" className="form-label">
                            Optional Services
                        </label>
                        <Select
                            id="selServices"
                            name="selServices"
                            //options={updatedOptServOpt}

                            options={
                                updatedOptServOpt.length > 0
                                    ? updatedOptServOpt
                                    : placeholderOption
                            }
                            onChange={(o, m) => onOptServChange(o)}
                        />
                    </div>
                    <div className="col-2">
                        <label htmlFor="txtServiceQuant" className="form-label">
                            Quantity
                        </label>
                        <input
                            id="txtServiceQuant"
                            name="txtServiceQuant"
                            type="number"
                            className="form-control"
                            onChange={(e) => {
                                onQuantityChange(e.currentTarget.valueAsNumber);
                            }}
                        />
                    </div>
                    <div className="col-2">
                        <span style={{ height: "10px" }}></span>
                        <button
                            className={"btn btn-primary"}
                            type="button"
                            onClick={() => {
                                onAddButton();
                            }}
                        >
                            Add
                        </button>
                    </div>
                </div>
                <div className="row mb-3">
                    <div
                        className="accordion accordion-flush"
                        id="accordionFlushExample"
                    >
                        <div className="accordion-item">
                            <h2
                                className="accordion-header"
                                id="flush-headingOne"
                            >
                                <button
                                    className="accordion-button"
                                    type="button"
                                    data-bs-toggle="collapse"
                                    data-bs-target="#flush-collapseOne"
                                    aria-expanded="true"
                                    aria-controls="flush-collapseOne"
                                >
                                    Attachments
                                </button>
                            </h2>
                            <div
                                id="flush-collapseOne"
                                className="accordion-collapse collapse show"
                                aria-labelledby="flush-headingOne"
                                data-bs-parent="#accordionFlushExample"
                            >
                                <div className="accordion-body">
                                    <MaterialTable
                                        data={updatedDocument.attachments ?? []}
                                        options={{
                                            search: false,
                                            showTitle: false,
                                            paging: false,
                                        }}
                                        columns={[
                                            {
                                                title: "Filename",
                                                field: "filename",
                                                editComponent: (
                                                    props: EditComponentProps<APIModels.Document>
                                                ) => {
                                                    return (
                                                        <>
                                                            <input
                                                                id={
                                                                    "attach-upload-" +
                                                                    props
                                                                        .rowData
                                                                        .attachments
                                                                        ?.length
                                                                }
                                                                type="file"
                                                                style={{
                                                                    display:
                                                                        "none",
                                                                }}
                                                                onChange={(
                                                                    fileInput
                                                                ) => {
                                                                    props.rowData.filename =
                                                                        fileInput.currentTarget.files![0].name;
                                                                    props.onRowDataChange(
                                                                        props.rowData
                                                                    );
                                                                    props.rowData.referenceNumber =
                                                                        props.rowData.filename;
                                                                }}
                                                            />
                                                            <label
                                                                htmlFor={
                                                                    "attach-upload-" +
                                                                    props
                                                                        .rowData
                                                                        .attachments
                                                                        ?.length
                                                                }
                                                            >
                                                                {props.rowData
                                                                    .filename ??
                                                                    "Click to Upload File"}
                                                            </label>
                                                        </>
                                                    );
                                                },
                                            },
                                            {
                                                title: "Confidential",
                                                field: "documentTypeId",
                                                editComponent:
                                                    editSecuritySelect,
                                            },
                                        ]}
                                        editable={{
                                            onRowAdd: (newData) =>
                                                new Promise(
                                                    (resolve, _reject) => {
                                                        const attachments = [
                                                            ...(updatedDocument.attachments ??
                                                                []),
                                                        ];
                                                        console.log(
                                                            updatedDocument
                                                        );
                                                        setUpdatedDocument({
                                                            ...updatedDocument,
                                                            attachments:
                                                                attachments,
                                                        });
                                                        resolve(null);
                                                    }
                                                ),
                                            onRowUpdate: (newData, oldData) =>
                                                new Promise(
                                                    (resolve, reject) => {
                                                        const index =
                                                            updatedDocument.attachments?.indexOf(
                                                                oldData!
                                                            );

                                                        const attachments =
                                                            updatedDocument.attachments?.map(
                                                                (item, idx) => {
                                                                    if (
                                                                        idx ===
                                                                        index
                                                                    ) {
                                                                        // Replacing with a new item, so the other component can understand it's a new item and re-render it.
                                                                        return {
                                                                            ...newData,
                                                                        };
                                                                    }

                                                                    // Return the same item, so the other component do not render since it's the same item.
                                                                    return item;
                                                                }
                                                            );
                                                        setUpdatedDocument({
                                                            ...updatedDocument,
                                                            attachments:
                                                                attachments,
                                                        });
                                                        resolve(null);
                                                    }
                                                ),
                                            onRowDelete: (oldData) =>
                                                new Promise(
                                                    (resolve, _reject) => {
                                                        const attachments = [
                                                            ...(updatedDocument.attachments ??
                                                                []),
                                                        ];
                                                        attachments.splice(
                                                            attachments.indexOf(
                                                                oldData
                                                            ),
                                                            1
                                                        );
                                                        setUpdatedDocument({
                                                            ...updatedDocument,
                                                            attachments:
                                                                attachments,
                                                        });
                                                        resolve(null);
                                                    }
                                                ),
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="primary"
                    onClick={() => {
                        if (updatedDocument.optionalServices) {
                            updateDocument(updatedDocument);
                        } else {
                            const tempDocument = updatedDocument;
                            tempDocument.optionalServices = [];
                            updateDocument(tempDocument);
                        }
                        // clear out the Document updatedDocument state
                        setUpdatedDocument({
                            submissionID: updatedDocument.submissionID!,
                        } as APIModels.Document);
                        close();
                    }}
                >
                    <i className="" /> Save
                </Button>
                <Button
                    variant="danger"
                    onClick={() => {
                        setUpdatedDocument({
                            submissionID: updatedDocument.submissionID!,
                        } as APIModels.Document);
                        close();
                    }}
                >
                    <i className="" /> Cancel
                </Button>
                {errorMessage !== "" ? (
                    <div className="text-danger">{errorMessage}</div>
                ) : (
                    ""
                )}
            </Modal.Footer>
        </Modal>
    );
}
